import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LucideShieldAlert, LucideX } from '@lucide/angular';

import { AuthService } from '../../../core/auth/auth.service';
import { ForbiddenDialogService } from '../../../core/dialogs/forbidden-dialog.service';
import { ButtonComponent } from '../button/button.component';

/**
 * App-wide modal for HTTP 403 responses. Keeps the user on the current page
 * while explaining they lack permission and offering a path to re-authenticate
 * or contact support.
 */
@Component({
  selector: 'g-forbidden-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, LucideShieldAlert, LucideX],
  template: `
    @if (dialog.state().open) {
      <div
        class="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
        role="presentation"
        (click)="onBackdropClick($event)"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="forbidden-dialog-title"
          class="flex w-full max-w-md flex-col rounded-t-2xl border border-border-subtle bg-surface-base shadow-xl sm:rounded-2xl"
        >
          <header
            class="flex items-start justify-between gap-3 border-b border-border-subtle px-5 py-4"
          >
            <div class="flex items-start gap-3">
              <span
                class="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-status-warning/15 text-status-warning"
                aria-hidden="true"
              >
                <svg lucideShieldAlert size="20"></svg>
              </span>
              <div>
                <h2 id="forbidden-dialog-title" class="text-base font-semibold text-text-primary">
                  Access denied
                </h2>
                <p class="mt-1 text-sm text-text-secondary">{{ dialog.state().message }}</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close"
              class="-mr-2 -mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-surface-sunken hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
              (click)="dialog.close()"
            >
              <svg lucideX size="18" aria-hidden="true"></svg>
            </button>
          </header>

          <div class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:justify-end">
            <g-button variant="secondary" (click)="dialog.close()">Stay on this page</g-button>
            <g-button (click)="loginAsSomeoneElse()">Log in as someone else</g-button>
          </div>

          <p class="border-t border-border-subtle px-5 py-3 text-xs text-text-secondary">
            Need help?
            <a
              href="mailto:support@growzz.com"
              class="text-brand-700 underline-offset-2 hover:underline"
              >Contact support</a
            >.
          </p>
        </div>
      </div>
    }
  `,
})
export class ForbiddenDialogComponent {
  protected readonly dialog = inject(ForbiddenDialogService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.dialog.close();
    }
  }

  protected loginAsSomeoneElse(): void {
    this.dialog.close();
    const targetUrl = this.router.url && this.router.url !== '/login' ? this.router.url : '/';
    void this.auth.loginWithRedirect({ targetUrl, promptLogin: true });
  }
}
